import React, { useState, useEffect } from 'react';
import { Plus, X, Calendar, Phone, Mail, MessageSquare } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import axios from '../api/axios';
import toast from 'react-hot-toast';
import VehicleSelector from './jobcards/VehicleSelector';
import CustomerModal from './jobcards/CustomerModal';
import { formatDate } from '../utils/dateFormat';

const ServiceReminder = () => {
  const location = useLocation();
  const [reminders, setReminders] = useState([]);
  
  const [customers, setCustomers] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customerName: '',
    contact: '',
    email: '',
    vehicleId: '',
    reminderDate: '',
    reminderTime: '',
    serviceType: '',
    priority: 'MEDIUM',
    mileageThreshold: '',
    notes: '',
    channels: [],
    assignedTo: ''
  });
  const [showVehicleSelector, setShowVehicleSelector] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  useEffect(() => {
    fetchReminders();
  }, []);

  useEffect(() => {
    // Handle edit reminder from navigation state
    if (location.state?.editReminder) {
      handleEdit(location.state.editReminder);
      // Clear the state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchVehicles = async () => {
    try {
      const response = await axios.get('/api/vehicles?limit=100');
      // Note: vehicles are handled by VehicleSelector component
    } catch (error) {
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get('/api/customers?limit=100');
      setCustomers(response.data.customers || []);
    } catch (error) {
    }
  };

  const [services, setServices] = useState([]);
  const [members, setMembers] = useState([]);
  const [currency, setCurrency] = useState('AED');

  const fetchServices = async () => {
    try {
      const [servicesRes, orgRes] = await Promise.all([
        axios.get('/api/services'),
        axios.get('/api/organization')
      ]);
      setServices(servicesRes.data.services || servicesRes.data || []);
      setCurrency(orgRes.data?.currency_code || 'AED');
    } catch (error) {
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await axios.get('/api/members');
      setMembers(response.data.members || response.data || []);
    } catch (error) {
    }
  };

  const fetchReminders = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/reminders');
      setReminders(response.data.reminders || []);
    } catch (error) {
      toast.error('Failed to fetch reminders');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // First, find or create customer
      let customerId;
      try {
        // Try to find existing customer by contact
        const customerResponse = await axios.get(`/api/customers?contact=${formData.contact}`);
        if (customerResponse.data.customers && customerResponse.data.customers.length > 0) {
          customerId = customerResponse.data.customers[0].id;
        } else {
          // Create new customer
          const newCustomer = await axios.post('/api/customers', {
            name: formData.customerName,
            phone: formData.contact
          });
          customerId = newCustomer.data.id;
        }
      } catch (customerError) {
        // If customer API fails, create customer
        const newCustomer = await axios.post('/api/customers', {
          name: formData.customerName,
          phone: formData.contact
        });
        customerId = newCustomer.data.id;
      }

      // Validate vehicle
      if (!selectedVehicle) {
        toast.error('Please select a vehicle');
        setLoading(false);
        return;
      }
      
      let vehicleId = selectedVehicle.id;
      
      // Create new vehicle if needed
      if (selectedVehicle.isNew) {
        const newVehicleData = {
          customerId,
          brandId: selectedVehicle.brandId,
          modelId: selectedVehicle.modelId,
          plateNumber: selectedVehicle.plateNo
        };
        const vehicleRes = await axios.post('/api/vehicles', newVehicleData);
        vehicleId = vehicleRes.data.id;
      }
      
      // Ensure vehicleId is a valid integer
      if (!vehicleId || isNaN(parseInt(vehicleId))) {
        toast.error('Invalid vehicle selection');
        setLoading(false);
        return;
      }
      
      // Prepare reminder data with correct field names
      const dueDateTime = new Date(`${formData.reminderDate}T${formData.reminderTime || '09:00'}:00`);
      const reminderData = {
        customerId: customerId,
        vehicleId: parseInt(vehicleId),
        serviceType: formData.serviceType,
        dueDate: dueDateTime.toISOString(),
        priority: formData.priority,
        assignedTo: formData.assignedTo || null,
        mileageThreshold: formData.mileageThreshold || null,
        notes: formData.notes || null,
        channels: formData.channels.length > 0 ? formData.channels.join(',') : null
      };

      if (editingReminder) {
        await axios.put(`/api/reminders/${editingReminder.id}`, reminderData);
        toast.success('Reminder updated successfully');
      } else {
        await axios.post('/api/reminders', reminderData);
        toast.success('Reminder created successfully');
      }
      setShowDrawer(false);
      setEditingReminder(null);
      setSelectedVehicle(null);
      setFormData({
        customerName: '',
        contact: '',
        email: '',
        vehicleId: '',
        reminderDate: '',
        reminderTime: '',
        serviceType: '',
        priority: 'MEDIUM',
        mileageThreshold: '',
        notes: '',
        channels: [],
        assignedTo: ''
      });
      fetchReminders();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create reminder');
    } finally {
      setLoading(false);
    }
  };

  const handleChannelChange = (channel) => {
    setFormData(prev => ({
      ...prev,
      channels: prev.channels.includes(channel)
        ? prev.channels.filter(c => c !== channel)
        : [...prev.channels, channel]
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'customerName') {
      const customer = customers.find(c => c.name === value);
      setSelectedCustomer(customer);
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        contact: customer ? (customer.phone || customer.contact) : '',
        email: customer ? (customer.email || '') : ''
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleEdit = (reminder) => {
    setEditingReminder(reminder);
    setSelectedVehicle(reminder.vehicle);
    const dueDateTime = new Date(reminder.dueDate);
    setFormData({
      customerName: reminder.customer?.name || '',
      contact: reminder.customer?.contact || '',
      email: reminder.customer?.email || '',
      vehicleId: reminder.vehicleId?.toString() || '',
      reminderDate: reminder.dueDate ? reminder.dueDate.split('T')[0] : '',
      reminderTime: dueDateTime.toTimeString().slice(0, 5),
      serviceType: reminder.serviceType || '',
      priority: 'MEDIUM',
      mileageThreshold: '',
      notes: '',
      channels: [],
      assignedTo: ''
    });
    setShowDrawer(true);
    fetchVehicles();
    fetchCustomers();
    fetchServices();
    fetchMembers();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this reminder?')) return;
    try {
      await axios.delete(`/api/reminders/${id}`);
      toast.success('Reminder deleted successfully');
      fetchReminders();
    } catch (error) {
      toast.error('Failed to delete reminder');
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Main Content */}
      <div className="flex-1 p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Service Reminder</h1>
          <button
            onClick={() => {
              setShowDrawer(true);
              setEditingReminder(null);
              fetchVehicles();
              fetchCustomers();
              fetchServices();
              fetchMembers();
            }}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Reminder
          </button>
        </div>

        {/* Reminders Table */}
        <div className="bg-white rounded-lg shadow border overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-pulse space-y-4">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="h-4 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          ) : reminders.length === 0 ? (
            <div className="p-8 text-center">
              <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No reminders found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reminder Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sub Service</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(reminders || []).map((reminder) => (
                    <tr key={reminder.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {reminder.customer?.name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {reminder.customer?.contact || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {reminder.vehicle ? `${reminder.vehicle.make} ${reminder.vehicle.model} - ${reminder.vehicle.plateNumber || 'No Plate'}` : 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {formatDate(reminder.dueDate)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {reminder.serviceType}
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">
                          PENDING
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(reminder)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDelete(reminder.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Right Drawer */}
      {showDrawer && (
        <div className="w-96 bg-white shadow-lg border-l">
          <div className="flex justify-between items-center p-6 border-b">
            <h2 className="text-lg font-semibold">{editingReminder ? 'Edit Reminder' : 'New Reminder'}</h2>
            <button
              onClick={() => setShowDrawer(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name *</label>
              <div className="flex space-x-2">
                <select
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleChange}
                  required
                  className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Customer</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.name}>
                      {customer.name} - {customer.contact}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowCustomerModal(true)}
                  className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                  title="Create New Customer"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer Contact *</label>
              <input
                type="tel"
                name="contact"
                value={formData.contact}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                readOnly={customers.some(c => c.name === formData.customerName)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                readOnly={customers.some(c => c.name === formData.customerName)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Select Vehicle *</label>
              <div
                onClick={() => setShowVehicleSelector(true)}
                className="w-full px-3 py-2 border rounded-md cursor-pointer hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {selectedVehicle ? (
                  <span className="text-gray-900">
                    {selectedVehicle.brand?.name || selectedVehicle.brand} {selectedVehicle.model?.name || selectedVehicle.model}
                    {(selectedVehicle.plateNo || selectedVehicle.plateNumber) && ` - ${selectedVehicle.plateNo || selectedVehicle.plateNumber}`}
                  </span>
                ) : (
                  <span className="text-gray-400">Click to select vehicle</span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reminder Date *</label>
                <input
                  type="date"
                  name="reminderDate"
                  value={formData.reminderDate}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reminder Time</label>
                <input
                  type="time"
                  name="reminderTime"
                  value={formData.reminderTime}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Service Type *</label>
              <select
                name="serviceType"
                value={formData.serviceType}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Service</option>
                {services.map(service => (
                  <option key={service.id} value={service.name}>
                    {service.name} - {currency} {service.price || 0}
                  </option>
                ))}
                <option value="oil-change">Oil Change</option>
                <option value="brake-service">Brake Service</option>
                <option value="ac-service">AC Service</option>
                <option value="general-maintenance">General Maintenance</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="URGENT">Urgent</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
              <select
                name="assignedTo"
                value={formData.assignedTo}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Technician</option>
                {members.map(member => (
                  <option key={member.id} value={member.id}>
                    {member.name} - {member.specialization || member.role}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mileage Threshold</label>
              <input
                type="number"
                name="mileageThreshold"
                value={formData.mileageThreshold}
                onChange={handleChange}
                placeholder="e.g., 10000 km"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                placeholder="Additional notes or instructions..."
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Channel</label>
              <div className="space-y-2">
                {[
                  { id: 'email', label: 'Email', icon: Mail },
                  { id: 'sms', label: 'SMS', icon: MessageSquare },
                  { id: 'call', label: 'Call', icon: Phone }
                ].map(({ id, label, icon: Icon }) => (
                  <label key={id} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.channels.includes(id)}
                      onChange={() => handleChannelChange(id)}
                      className="mr-2"
                    />
                    <Icon className="w-4 h-4 mr-2 text-gray-500" />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setFormData({
                  customerName: '',
                  contact: '',
                  email: '',
                  vehicleId: '',
                  reminderDate: '',
                  reminderTime: '',
                  serviceType: '',
                  priority: 'MEDIUM',
                  mileageThreshold: '',
                  notes: '',
                  channels: [],
                  assignedTo: ''
                })}
                className="flex-1 px-4 py-2 border rounded-md hover:bg-gray-50"
              >
                Reset
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Submitting...' : editingReminder ? 'Update' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Vehicle Selector Modal */}
      {showVehicleSelector && (
        <VehicleSelector
          customerId={selectedCustomer?.id || null}
          onSelect={(vehicle) => {
            setSelectedVehicle(vehicle);
            setFormData({...formData, vehicleId: vehicle.id});
            setShowVehicleSelector(false);
          }}
          onClose={() => setShowVehicleSelector(false)}
        />
      )}

      {/* Customer Modal */}
      {showCustomerModal && (
        <CustomerModal
          onSuccess={(newCustomer) => {
            setCustomers(prev => [...prev, newCustomer]);
            setSelectedCustomer(newCustomer);
            setFormData(prev => ({
              ...prev,
              customerName: newCustomer.name,
              contact: newCustomer.phone || newCustomer.contact,
              email: newCustomer.email || ''
            }));
            setShowCustomerModal(false);
          }}
          onClose={() => setShowCustomerModal(false)}
        />
      )}
    </div>
  );
};

export default ServiceReminder;
